// @ts-check

// Vendor imports
const React = require('react')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')
const ViewportObserver = require('client-v2/common/utils/viewport-observer')

// IntersectionObserver options

/**
 * @typedef {Object} IntersectionObserverOptions
 * @property {number} threshold
 * @property {Element|null} [root]
 * @property {string} [rootMargin]
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
 */

/** @type {IntersectionObserverOptions} */
const FULLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS = { threshold: 1.00 }
/** @type {IntersectionObserverOptions} */
const MOSTLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS = { threshold: 0.50 }
/** @type {IntersectionObserverOptions} */
const PARTIALLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS = { threshold: 0.10 }

// ViewportObserver cache

/** @type {WeakMap<IntersectionObserverOptions, ViewportObserver>} */
const viewportObserverCache = new WeakMap()

/**
 *
 * @typedef {Object} ViewportObserverEntryExtra
 * @property {() => void} onVisible
 * @property {() => void} onHidden
 */

/**
 * @param {IntersectionObserverOptions} intersectionObserverOptions
 * @returns {ViewportObserver}
 */
function getViewportObserver(intersectionObserverOptions) {
  const cached = viewportObserverCache.get(intersectionObserverOptions)
  if (cached) {
    return cached
  }
  /**
   * @param {Element} _element
   * @param {ViewportObserverEntryExtra} onVisible
   */
  function visibleCallback(_element, { onVisible }) { onVisible() }
  /**
   * @param {Element} _element
   * @param {ViewportObserverEntryExtra} onVisible
   */
  function hiddenCallback(_element, { onHidden }) { onHidden() }
  const created = new ViewportObserver({
    intersectionObserverOptions,
    visibleCallback,
    hiddenCallback
  })
  viewportObserverCache.set(intersectionObserverOptions, created)
  return created
}

// Hook definition

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {IntersectionObserverOptions} options.intersectionObserverOptions
 * @param {boolean} [options.isDisabled]
 * @param {boolean} [options.unobserveOnVisible]
 * @param {() => void} [options.onHidden]
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean ]}
 */
function useViewportObserver({
  elementRef,
  initialIsVisible = false,
  intersectionObserverOptions,
  isDisabled = false,
  unobserveOnVisible = false,
  onHidden,
  onVisible
}) {
  const [ isVisible, setVisible ] = React.useState(initialIsVisible)
  const handleVisible = React.useCallback(
    () => {
      if (!isVisible) {
        setVisible(true)
        if (onVisible) {
          onVisible()
        }
      }
    },
    [ isVisible, setVisible, onVisible ]
  )
  const handleHidden = React.useCallback(
    () => {
      if (isVisible) {
        setVisible(false)
        if (onHidden) {
          onHidden()
        }
      }
    },
    [ isVisible, setVisible, onHidden ]
  )
  useClientEffect(
    () => {
      const el = elementRef.current
      if (isDisabled || !el) {
        return
      }
      const viewportObserver = getViewportObserver(intersectionObserverOptions)
      viewportObserver.observe(el, {
        onVisible() {
          handleVisible()
          if (unobserveOnVisible) {
            viewportObserver.unobserve(el)
          }
        },
        onHidden() {
          handleHidden()
        }
      })
      return () => {
        viewportObserver.unobserve(el)
      }
    },
    [
      elementRef, isDisabled, intersectionObserverOptions,
      unobserveOnVisible, handleVisible, handleHidden
    ]
  )
  return [ isVisible ]
}

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {boolean} [options.isDisabled]
 * @param {boolean} [options.unobserveOnVisible]
 * @param {() => void} [options.onHidden]
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean ]}
 */
function useFullyVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: FULLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS
  })
}

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {boolean} [options.isDisabled]
 * @param {boolean} [options.unobserveOnVisible]
 * @param {() => void} [options.onHidden]
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean ]}
 */
function useMostlyVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: MOSTLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS
  })
}

/**
 * @param {Object} options
 * @param {React.RefObject<Element>} options.elementRef
 * @param {boolean} [options.initialIsVisible]
 * @param {boolean} [options.isDisabled]
 * @param {boolean} [options.unobserveOnVisible]
 * @param {() => void} [options.onHidden]
 * @param {() => void} [options.onVisible]
 * @returns {[ boolean ]}
 */
function usePartiallyVisibleViewportObserver(options) {
  return useViewportObserver({
    ...options,
    intersectionObserverOptions: PARTIALLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS
  })
}

module.exports = {
  // Pre-defined IntersectionObserver options
  FULLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS,
  PARTIALLY_VISIBLE_INTERSECTION_OBSERVER_OPTIONS,
  // Hooks
  useFullyVisibleViewportObserver,
  useMostlyVisibleViewportObserver,
  usePartiallyVisibleViewportObserver,
  useViewportObserver
}
